import { ContentExpander, Grid, NumberField } from '@components';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import { RateCardFormErrors, RateCardSettingsTypes, RateCardTypes } from '@state';
import { RateCardType, TRateCardModifierSet } from '@types';
import { useRecoilValue } from 'recoil';
import styles from './Modifiers.module.scss';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	type: RateCardType
	isDisabled?: boolean;
	itemTypeId?: string;
}

export const AdminBookingFeesPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		itemTypeId
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		itemTypeId,
		isSettings: !!isSettings,
		type
	});

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.[key]?.value?._errors?.[0] || '';
	}

	const updateValue = (key:string, newValue: string) => {
		const update = {
			...modifiers,
			[key]: {
				type: 'FIXED',
				value: newValue
			}
		} as TRateCardModifierSet;
		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}

		setRateCardModifiers(newModifiers);
		clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.${key}.value`)
	}

	const handleOnBlur = (key: string, newValue: string) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateValue(key, value);
		}
		if (newValue.startsWith('.')) {
			updateValue(key, `0${newValue}`)
		}
	}

	const getConfiguredCount = () => {
		const fee = modifiers?.admin_standard_booking_fee;
		if (!fee) return 0;
		return parseFloat(`${fee.value ?? ''}`) > 0 ? 1 : 0;
	}

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Admin booking fees
				<ModifierBadge count={getConfiguredCount()} />
			</div>}
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Grid columns={2} gap={'16px'} alignItems='start'>
					<NumberField
						key={`admin_standard_booking_fee`}
						name={`admin_standard_booking_fee`}
						value={`${modifiers?.admin_standard_booking_fee?.value || ''}`}
						onChange={(e) => updateValue(e.target.name, e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={`Standard booking fee`}
						keepErrorSpace={false}
						errorMessage={hasError('admin_standard_booking_fee')}
						hasError={!!hasError('admin_standard_booking_fee')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
					/>
				</Grid>
			</div>

		</ContentExpander>
	);
}